// Libraries
import React, {useEffect, useState} from 'react';
import {Row, Form, Modal, Switch, Select, SelectProps} from 'antd';
import {useAppDispatch} from '../../store/store';
import {useSelector} from 'react-redux';

// Store
import * as storeSelectors from '../../store/redux/selectors/storeSelectors';
import { create, update } from '../../store/redux/actions/storeActions';

// Types
import {StoreType, ZoneType} from "../../types/storeTypes";
import { ParamType } from '../../types/sharedTypes';

// Components
import SelectComponent from '../subComponents/SelectComponent';
import ButtonComponent from '../subComponents/ButtonComponent';
import FormItemComponent from '../subComponents/FormItemComponent';
import InputComponent from '../subComponents/InputComponent';

type PropsType = {
  isEditing?: boolean;
  storeToEdit?: StoreType;
  onToggleModal: (visible: boolean) => void;
};
const StoresFormModal = (props: PropsType) => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const [zoneOptions, setZoneOptions] = useState<SelectProps['options']>([]);
  const { isEditing, storeToEdit, onToggleModal } = props;
  const { locations, alliances } = useSelector(storeSelectors.storeParamsSelector);
  const shipFromStoreValue = Form.useWatch('shipFromStore', form);

  useEffect(() => {
    const newZoneOptions: SelectProps['options'] = [];

    locations.map((location: ParamType) => {
      newZoneOptions.push(
        { key: location.id, value: location.id, label: location.value }
      );
    });

    setZoneOptions(newZoneOptions);
  }, [storeToEdit, locations]);

  const handleSubmit = (store: StoreType) => {
    if(isEditing){
      store.storeId = storeToEdit ? storeToEdit.storeId : '';
      store.zones = store.multipleZones ? store.multipleZones : [];
      store.multipleZones = undefined;

      dispatch(update(store));
    } else {
      store.isActive = true;
      store.zones = store.multipleZones ? store.multipleZones : [];

      dispatch(create(store));
    }
    onToggleModal(false);
  }

  return (
    <Modal title={`${isEditing ? 'Editar' : 'Crear'} centro`}
           open={true} maskClosable closable={false} keyboard={false} destroyOnClose={true}
           footer={[
             <ButtonComponent key={'cancel'} text={'Cerrar'}
                              className={'w-32 h-8 bg-white border border-grey-dkt-300 leading-4 text-grey-dkt-700 ' +
                                'hover:bg-grey-dkt-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-dkt-400'}
                              onClick={() => onToggleModal(false)} />,
             <ButtonComponent key={'submit'} text={`${isEditing ? 'Editar' : 'Crear'}`}
                              className={'w-32 h-8 text-white bg-blue-dkt-400 hover:bg-blue-dkt-500 focus:outline-none focus:ring-2 ' +
                                'focus:ring-offset-2 focus:ring-blue-dkt-300 after:content-none'}
                              onClick = {() => form.submit()}/>,
           ]}>
      <Form layout={"vertical"}
            form={form}
            initialValues={storeToEdit ?? {}}
            onFinish={handleSubmit}>
        <Row className={'grid grid-cols-1 md:grid-cols-2 gap-x-2'}>
          {isEditing &&
            <FormItemComponent name={'isActive'}
                               label={'Estado de la tienda'}
                               required={false}
                               valuePropName={'checked'}
                               child={<Switch checkedChildren={"Activa"}
                                              unCheckedChildren={"Inactiva"} />}
            />
          }

          <FormItemComponent name={'shipFromStore'}
                              label={'Despacho Local (DL)'}
                              required={false}
                              valuePropName={'checked'}
                              child={<Switch checkedChildren={"SI"}
                                             unCheckedChildren={"NO"} />}
          />

          <FormItemComponent name={'useBarcode'}
                              className={!isEditing ? '' : 'col-span-2'}
                              label={'Usar código de barras'}
                              required={false}
                              valuePropName={'checked'}
                              child={<Switch checkedChildren={"SI"}
                                             unCheckedChildren={"NO"} />}
          />

          <FormItemComponent name={'name'}
                             label={'Nombre'}
                             required={true}
                             child={<InputComponent />}
          />

          <FormItemComponent name={'longName'}
                             label={'Nombre largo'}
                             required={true}
                             child={<InputComponent />}
          />

          <FormItemComponent name={'code'}
                             label={'Código'}
                             required={true}
                             child={<InputComponent />}
          />

          <FormItemComponent name={'address'}
                             label={'Dirección'}
                             required={true}
                             child={<InputComponent />}
          />

          <FormItemComponent name={'phone'}
                             label={'Teléfono'}
                             required={true}
                             child={<InputComponent />}
          />

          <FormItemComponent name={'locationId'}
                             label={'Código postal'}
                             required={true}
                             child={<SelectComponent list={locations}
                                                     showSearch={true}
                                                     searchFieldName={'children'} />}
          />

          <FormItemComponent name={'forcedAllianceId'}
                             label={'Forzar por la alianza'}
                             required={false}
                             child={<SelectComponent list={alliances}
                                                     allowClear={true}
                                                     searchFieldName={'children'} />}
          />

          <FormItemComponent name={'multipleZones'}
                             label={'Zonas'}
                             required={shipFromStoreValue ?? false}
                             className={'col-span-2'}
                             initialValue={storeToEdit?.zones.map((zone) =>
                               typeof zone === 'string' ? null : zone.locationId
                             )}
                             getValueFromEvent={(values: string[]) => {
                               const cleanValues =
                                 values.map((value) => value.replaceAll(' ', ''));

                               return cleanValues.map((value) => {
                                 const option =
                                   zoneOptions?.find((o) => o.label === value);

                                 return option ? option.value : value;
                               });
                             }}
                             newRules={[
                               {
                                 validator: (_, values: string[]) => {
                                   const allowedLabels =
                                     zoneOptions?.map((option) => option.value)
                                     ?? [];
                                   const invalid = values?.filter((value) => !allowedLabels.includes(value));

                                   if (shipFromStoreValue && invalid && invalid.length > 0) {
                                     return Promise.reject(
                                       new Error(`Los siguientes valores no son válidos: ${invalid.join(", ")}`)
                                     );
                                   }

                                   return Promise.resolve();
                                 },
                               },
                             ]}
                             child={<Select mode="tags"
                                            allowClear
                                            filterOption={(input, option) =>
                                              (option?.label as string ?? '').includes(input)
                                            }
                                            tokenSeparators={[","]}
                                            options={zoneOptions}
                             />}
          />
        </Row>
      </Form>
    </Modal>
  );
}

export default StoresFormModal;